import { Injectable } from '@angular/core';
import { GridHeader } from '../../tracking-overview/interfaces/grid';
import { GroupTrackingGridData } from '../filter-samples-info';
import * as XLSX from 'xlsx';
import { cloneDeep } from 'lodash';

@Injectable()
export class SamplesDataDownloadService {

    private downloadAsCsv = false;

    constructor() {
    }
    /**
     * 
     * @param id 
     * @param gridData 
     * @returns GroupTrackingGridData[]
     * add grid list headers on sample-column-service.ts
     */
    public getMappedData(id: number, gridData: GroupTrackingGridData[]) {
        switch (id) {
            case 1:
                return this.getAllSamplesColumnMapping(gridData);
            case 2:
                return this.getNotReceivedColumnMapping(gridData);
            case 3:
                return this.getReceivedSamplesListColumnMapping(gridData);
            case 4:
                return this.getShippedColumnMapping(gridData);
            case 5:
                return this.getInventoryColumnMapping(gridData);
            case 6:
                return this.getDiscardedColumnMapping(gridData);
            case 7:
                return this.getCancelledColumnMapping(gridData);
            case 8:
                return this.getShippedFromSiteColumnMapping(gridData);
            case 9:
                return this.getShipmentsOnHoldColumns(gridData);
            default:
                return this.getBasicGridColumnMapping(gridData);
        }
    }

    public getDownloadData(columnDefs: GridHeader[], gridData: GroupTrackingGridData[]) {
        let downloadData: any[] = [];
        gridData.map((gd) => {
            let downloadInfo: any = {};
            columnDefs.forEach(column => {
                downloadInfo[column.key as keyof GroupTrackingGridData] = gd[column.key as keyof GroupTrackingGridData];
            });
            let parentData = gd.parentDataForDownload && gd.parentDataForDownload.join();
            let childData = gd.childDataForDownload && gd.childDataForDownload.join();
            downloadInfo.parentDataForDownload = parentData && this.downloadAsCsv ? '\"' + parentData + '\"' : parentData;
            downloadInfo.childDataForDownload = childData && this.downloadAsCsv ? '\"' + childData + '\"' : childData;
            downloadData.push(downloadInfo);
        });
        return downloadData;
    }

    public exportData(gridColumns: GridHeader[], rawData: GroupTrackingGridData[], fileName: string, statusID: number, downloadAsCSV?: boolean, isDynamicView = false) {
        if (!rawData || rawData.length === 0) { return; }
        this.downloadAsCsv = downloadAsCSV;
        let clonedGridColumns = cloneDeep(gridColumns);
        clonedGridColumns.push({ displayText: 'Parent Barcode', key: 'parentDataForDownload' }, { displayText: 'Children Barcode', key: 'childDataForDownload' });
        let header = clonedGridColumns.filter(vdh => !vdh.skipForDownload).map(t => t.displayText);

        let clonedGridData = cloneDeep(rawData);
        clonedGridData = this.flatternGridData(clonedGridData);
        let downloadData: any;
        if (isDynamicView) {
            downloadData = this.getDownloadData(gridColumns, rawData);
        } else {
            downloadData = this.getMappedData(statusID, clonedGridData);
        }
        this.exportAsExcel(header, downloadData, fileName, downloadAsCSV);
    }

    private exportAsExcel(header: string[], data: any, fileNameToDownload: string, downloadAsCSV?: boolean) {
        const wb = XLSX.utils.book_new();
        const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet([]);
        XLSX.utils.sheet_add_aoa(ws, [header]);
        XLSX.utils.sheet_add_json(ws, data, { origin: 'A2', skipHeader: true });
        XLSX.utils.book_append_sheet(wb, ws);

        const format = downloadAsCSV ? '.csv' : '.xlsx';
        XLSX.writeFile(wb, fileNameToDownload + format);
    }

    private loadCommonMappings(data: GroupTrackingGridData) {
        return {
            protocolCode: data.protocolCode,
            region: data.region,
            projectNumber: data.projectNumber,
            investigatorNumber: data.investigatorNumber,
            investigatorName: data.investigatorName,
            countryName: data.countryName,
            screeningNumber: data.screeningNumber,
            patientNumber: data.patientNumber,
            specimenCollectionDate: data.specimenCollectionDate,
            specimenCollectionTime: data.specimenCollectionTime,
            protocolVisitDescription: data.protocolVisitDescription,
            protocolVisitCode: data.protocolVisitCode,
            accessionNumber: data.accessionNumber,
            containerNumber: data.containerNumber,
            barcode: data.barcode,
            specimenType: data.specimenType,
        };
    }

    private loadExpectedDataMapping(data: GroupTrackingGridData) {
        return {
            kitReceivedDate: data.kitReceivedDate,
            kitReceivedTime: data.kitReceivedTime,
            containerReceivedDate: data.containerReceivedDate,
            containerReceivedTime: data.containerReceivedTime,
            expectedReceiptCondition: data.expectedReceiptCondition,
            actualReceiptCondition: data.actualReceiptCondition,
            containerLabel1: data.containerLabel1,
            containerLabel2: data.containerLabel2,
        };
    }

    private getBasicGridColumnMapping(gridData: GroupTrackingGridData[]) {
        return gridData.map((data) => {
            var commonMappings = this.loadCommonMappings(data);
            var expectedMappings = this.loadExpectedDataMapping(data);
            return {
                ...commonMappings,
                sampleStatus: data.sampleStatus,
                ...expectedMappings,
                cancelDescription: data.cancelDescription,
                ...this.addEndColumns(data)
            };
        });
    }

    private getNotReceivedColumnMapping(gridData: GroupTrackingGridData[]) {
        return gridData.map((data) => {
            var commonMappings = this.loadCommonMappings(data);
            return {
                ...commonMappings,
                kitReceivedDate: data.kitReceivedDate,
                kitReceivedTime: data.kitReceivedTime,
                expectedReceiptCondition: data.expectedReceiptCondition,
                expectedReturnState: data.expectedReturnState,
                containerLabel1: data.containerLabel1,
                containerLabel2: data.containerLabel2,
                daysPendingReceipt: data.daysPendingReceipt,
                notification1SentTo: data.notification1SentTo,
                notification1Sent: data.notification1Sent,
                notification1SentTime: data.notification1SentTime,
                notification2SentTo: data.notification2SentTo,
                notification2Sent: data.notification2Sent,
                notification2SentTime: data.notification2SentTime,
                notification3SentTo: data.notification3SentTo,
                notification3Sent: data.notification3Sent,
                notification3SentTime: data.notification3SentTime,
                ...this.addEndColumns(data)
            };
        });
    }

    private getShippedColumnMapping(gridData: GroupTrackingGridData[]) {
        return gridData.map((data) => {
            var commonMappings = this.loadCommonMappings(data);
            var expectedMappings = this.loadExpectedDataMapping(data);
            return {
                ...commonMappings,
                sampleStatus: data.sampleStatus,
                ...expectedMappings,
                sampleRegisteredDate: data.sampleRegisteredDate,
                sampleRegisteredTime: data.sampleRegisteredTime,
                shippedToLocation: data.shippedToLocation,
                shippedDate: data.shippedDate,
                shippedTime: data.shippedTime,
                airwayBillNumber: data.airwayBillNumber,
                specimenClassDescription: data.specimenClassDescription,
                ...this.addEndColumns(data)

            };
        });
    }

    private getInventoryColumnMapping(gridData: GroupTrackingGridData[]) {
        return gridData.map((data) => {
            var commonMappings = this.loadCommonMappings(data);
            var expectedMappings = this.loadExpectedDataMapping(data);
            return {
                ...commonMappings,
                sampleStatus: data.sampleStatus,
                ...expectedMappings,
                sampleRegisteredDate: data.sampleRegisteredDate,
                sampleRegisteredTime: data.sampleRegisteredTime,
                specimenClass: data.specimenClass,
                specimenClassDescription: data.specimenClassDescription,
                ...this.addEndColumns(data)
            };
        });
    }

    private getDiscardedColumnMapping(gridData: GroupTrackingGridData[]) {
        return gridData.map((data) => {
            var commonMappings = this.loadCommonMappings(data);
            var expectedMappings = this.loadExpectedDataMapping(data);
            return {
                ...commonMappings,
                ...expectedMappings,
                sampleRegisteredDate: data.sampleRegisteredDate,
                sampleRegisteredTime: data.sampleRegisteredTime,
                specimenClass: data.specimenClass,
                specimenClassDescription: data.specimenClassDescription,
                ...this.addEndColumns(data)
            };
        });
    }

    private getCancelledColumnMapping(gridData: GroupTrackingGridData[]) {
        return gridData.map((data) => {
            var commonMappings = this.loadCommonMappings(data);
            var expectedMappings = this.loadExpectedDataMapping(data);
            return {
                ...commonMappings,
                ...expectedMappings,
                cancelDescription: data.cancelDescription,
                ...this.addEndColumns(data)
            };
        });
    }

    private getShippedFromSiteColumnMapping(gridData: GroupTrackingGridData[]) {
        return gridData.map((data) => {
            return {
                protocolCode: data.protocolCode,
                region: data.region,
                projectNumber: data.projectNumber,
                investigatorNumber: data.investigatorNumber,
                investigatorName: data.investigatorName,
                countryName: data.countryName,
                screeningNumber: data.screeningNumber,
                patientNumber: data.patientNumber,
                protocolVisitDescription: data.protocolVisitDescription,
                protocolVisitCode: data.protocolVisitCode,
                accessionNumber: data.accessionNumber,
                containerNumber: data.containerNumber,
                barcode: data.barcode,
                containerLabel1: data.containerLabel1,
                containerLabel2: data.containerLabel2,
                specimenType: data.specimenType,
                shipmentCreatedDate: data.shipmentCreatedDate,
                shipmentCreatedTime: data.shipmentCreatedTime,
                shipmentSubmittedDate: data.shipmentSubmittedDate,
                shipmentSubmittedTime: data.shipmentSubmittedTime,
                scheduledShipmentDate: data.scheduledShipmentDate,
                scheduledShipmentTime: data.scheduledShipmentTime,
                airwayBillNumber: data.airwayBillNumber,
                courier: data.shipmentCourier,
                shippedToOrgName: data.shippedToOrganizationName,
                siteShipmentStatus: data.shipmentStatus,
                ...this.addEndColumns(data)
            };
        });
    }

    private getAllSamplesColumnMapping(gridData: GroupTrackingGridData[]) {
        return gridData.map((data) => {
            var commonMappings = this.loadCommonMappings(data);
            var expectedMapping = this.loadExpectedDataMapping(data);
            return {
                ...commonMappings,
                sampleStatus: data.sampleStatus,
                ...expectedMapping,
                cancelDescription: data.cancelDescription,
                shipmentCreatedDate: data.shipmentCreatedDate,
                shipmentCreatedTime: data.shipmentCreatedTime,
                shipmentSubmittedDate: data.shipmentSubmittedDate,
                shipmentSubmittedTime: data.shipmentSubmittedTime,
                scheduledShipmentDate: data.scheduledShipmentDate,
                scheduledShipmentTime: data.scheduledShipmentTime,
                airwayBillNumberColumn: data.airwayBillNumber,
                shipmentCourier: data.shipmentCourier,
                shippedToOrganizationName: data.shippedToOrganizationName,
                ...this.addEndColumns(data)
            };
        });
    }

    private getReceivedSamplesListColumnMapping(listData: GroupTrackingGridData[]) {
        return listData.map((data) => {
            var commonColumnMappings = this.loadCommonMappings(data);
            var endColumnMappings = this.loadExpectedDataMapping(data);
            return {
                ...commonColumnMappings,
                sampleStatus: data.sampleStatus,
                ...endColumnMappings,
                airwayBillNumber: data.airwayBillNumber,
                ...this.addEndColumns(data)
            };
        });
    }

    private getShipmentsOnHoldColumns(listData: GroupTrackingGridData[]) {
        return listData.map((data) => {
            return {
                protocolCode: data.protocolCode,
                region: data.region,
                projectNumber: data.projectNumber,
                countryName: data.countryName,
                investigatorName: data.investigatorName,
                investigatorNumber: data.investigatorNumber,
                screeningNumber: data.screeningNumber,
                patientNumber: data.patientNumber,
                accessionNumber: data.accessionNumber,
                specimenCollectionDate: data.specimenCollectionDate,
                protocolVisitCode: data.protocolVisitCode,
                protocolVisitDescription: data.protocolVisitDescription,
                kitReceivedDate: data.kitReceivedDate,
                daysWithActiveeQuery: data.daysOpen,
                eQueryType: data.eQueryType,
                samplesAffectedByQuery: data.samplesEffectedByQuery,
                eQueryOpenDate: data.eQueryOpenDate,
                ...this.addEndColumns(data)
            };
        });
    }


    private flatternGridData(clonedGridData: GroupTrackingGridData[]) {
        let reportData: GroupTrackingGridData[] = [];

        clonedGridData.forEach((gd: GroupTrackingGridData) => {
            reportData.push(gd);
            if (gd.expandData) {
                if (gd.expandData.child && gd.expandData.child.length) {
                    gd.childDataForDownload = [];
                    gd.expandData.child.forEach((children: GroupTrackingGridData) => {
                        gd.childDataForDownload.push(children.barcode);
                    });
                }

                if (gd.expandData.parent && gd.expandData.parent.length) {
                    gd.parentDataForDownload = [];
                    gd.expandData.parent.forEach((parent: GroupTrackingGridData) => {
                        gd.parentDataForDownload.push(parent.barcode);
                    });
                }

                delete gd.expandData;
            }
        });
        return reportData;
    }

    private addEndColumns(gd: GroupTrackingGridData) {
        let parentData = gd.parentDataForDownload && gd.parentDataForDownload.join();
        let childData = gd.childDataForDownload && gd.childDataForDownload.join();
        return {
            parentDataForDownload: parentData && this.downloadAsCsv ? '\"' + parentData + '\"' : parentData,
            childDataForDownload: childData && this.downloadAsCsv ? '\"' + childData + '\"' : childData
        };
    }
}